import { bound } from "@frui.ts/helpers/dist";
import { Modules } from "config";
import { action, computed } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import ListViewModel from "viewModels/listViewModel";
import type { interfaces } from "inversify";
import type { IResults } from "data/types";
import ExemptionRepository from "repositories/exemptionRepository";
import ExemptionChangeResponse from "entities/exemptionChangeResponse";
import type FuelType from "entities/fuelType";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import type VehicleRegisterVehicleFuelType from "entities/vehicleRegisterVehicleFuelType";
import type VehicleRegisterVehicleStatus from "entities/vehicleRegisterVehicleStatus";
import PriceListService from "services/priceListService";
import EnumService from "services/enumService";
import ExemptionPropertyChangeDisplayType from "entities/exemptionPropertyChangeDisplayType";
import type ExemptionChangeOrigin from "entities/exemptionChangeOrigin";
import type ChargeChangeInitiator from "entities/chargeChangeInitiator";
import { emptyValue } from "utils/emptyValue";

export default class ExemptionChangesViewModel extends ListViewModel<ExemptionChangeResponse> {
  readonly modelName = "exemptionChanges";

  constructor(
    protected exemptionId: string,
    public localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: ExemptionRepository,
    private enumService: EnumService,
    private priceListService: PriceListService
  ) {
    super();
    this.navigationName = "";
  }

  protected async fetchData(): Promise<IResults<ExemptionChangeResponse>> {
    return await this.repository.getAllChanges(this.exemptionId);
  }

  @computed
  get transformedItems(): ExemptionChangeResponse[] {
    return this.items.flatMap(item => {
      return item;
    });
  }

  @computed
  get name() {
    return this.localizationService.translateModule(Modules.callCenter, "exemptionChanges.name");
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `exemptionChanges.${code}`, size);
  }
  @bound
  canShowDetail(exemption: ExemptionChangeResponse): boolean {
    return (
      exemption.changeType === ExemptionPropertyChangeDisplayType.VehicleRegisterVehicleIdChange ||
      exemption.changeType === ExemptionPropertyChangeDisplayType.VehicleRegisterVehiclePropertiesChange
    );
  }

  @bound
  onClose() {
    this.requestClose();
  }

  @bound
  getExemptionReason(exemptionReasonId: string) {
    const exemptionReasons = this.enumService.values("notifiable_exemption_reasons");

    return exemptionReasons.find(x => x.id === exemptionReasonId);
  }

  @action.bound
  handleShowDetail(exemption: ExemptionChangeResponse): void {
    exemption._showDetail = !exemption._showDetail;
  }

  translateChangeAttribute(attribute: keyof ExemptionChangeResponse) {
    return this.localizationService.translateAttribute("exemptionVehicleRegister", attribute);
  }

  translateFuelType(priceListId: string): string {
    const fuelTypeId = this.priceListService.priceListItemById(priceListId)?.fuelTypeId as FuelType;

    const fuelType = this.enumService.values("fuel_types").find(fuelType => fuelType.id === fuelTypeId);
    if (!fuelType) {
      return "";
    }

    return this.localizationService.translateEnum("fuelType", fuelType.fuelType);
  }

  translateCountry(countryId: string) {
    return this.enumService.value("countries", countryId)?.name;
  }

  translateBoolean(boolean: string) {
    return this.localizationService.translateGeneral(`boolean.${boolean.toLowerCase()}`);
  }

  translateChangedExemptionProperty(
    exemption: ExemptionChangeResponse,
    exemptionPropertyChangeDisplayType: ExemptionPropertyChangeDisplayType
  ) {
    return this.localizationService.translateEnum("exemptionPropertyChangeDisplayType", exemptionPropertyChangeDisplayType);
  }

  translateChargeChangeInitiator(chargeChangeInitiator: ChargeChangeInitiator) {
    return this.localizationService.translateEnum("chargeChangeInitiator", chargeChangeInitiator);
  }

  translateExemptionChangeOrigin(exemptionChangeOrigin: ExemptionChangeOrigin) {
    return this.localizationService.translateEnum("exemptionChangeOrigin", exemptionChangeOrigin);
  }

  translateVehicleRegisterVehicleFuelType(fuelType: VehicleRegisterVehicleFuelType[]) {
    const translation = fuelType.map(f => this.localizationService.translateEnum("vehicleRegisterVehicleFuelType", f));
    return translation.length > 1 ? translation.join(", ") : translation[0];
  }

  translateVehicleRegisterVehicleClassifiedFuelType(classifiedFuelType: VehicleRegisterVehicleClassifiedFuelType) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleClassifiedFuelType", classifiedFuelType);
  }

  translateVehicleStatus(status: VehicleRegisterVehicleStatus) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleStatus", status);
  }

  translateExemptionReasonId(exemptionReasonId: string) {
    const matchingExemptionReason = this.getExemptionReason(exemptionReasonId);
    return matchingExemptionReason?.description ?? emptyValue;
  }

  static Factory({ container }: interfaces.Context) {
    return (exemptionId: string) =>
      new ExemptionChangesViewModel(
        exemptionId,
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(SecurityService),
        container.get(ExemptionRepository),
        container.get(EnumService),
        container.get(PriceListService)
      );
  }
}
